import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { createHdTransitGateLineVmFactory } from '../../../transits/transit-gate-line-vm.factory';
import { HdGateLineComponent } from '../gate-info/line/hd-gate-line.component';
import { HdGateLineViewModel } from '../gate-info/line/hd-gate-line.view-model';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdCycleCardSlot, HdCycleCardSource } from '../hd-cycle-cards.builder';
import { HdGateInfoService } from '../hd-gate-info.service';
import { HdPlanetInfoService } from '../hd-planet-info.service';

const T = 'humanDesign.chart.cycleCards';

/** Eén kaartplek: de volledige lijnkaart (`first`) of een verwijzing naar de kaart hoger in de opsomming. */
interface CardVm {
  readonly slot: HdCycleCardSlot;
  /** `null` wanneer de lijn (nog) geen vertaling heeft; de plek valt dan weg. */
  readonly lineVm: HdGateLineViewModel | null;
  readonly planetName: string;
  readonly seeAboveLabel: string;
  readonly seeAboveTooltip: string;
}

/**
 * De I Tjing-lijnkaarten onder een kanaal- of poortblokje: per planeetactivatie
 * de `hd-gate-line`-kaart (planeet als activatie in transit- of chart-stijl),
 * of een verwijsregel "zie 20.3 hierboven" wanneer dezelfde activatie al hoger
 * een kaart kreeg (dedup door de aanroeper via {@link HdCycleCardSlot.first}).
 *
 * **Lui**: zolang {@link visible} `false` is, bouwt het component geen VM's —
 * zo blijft het Geavanceerd-paneel licht met alle items dichtgeklapt. De
 * VM-factory cachet per `poort.lijn|planeet|zijde|oorsprong` per taal.
 *
 * Gedeeld door de keynote-secties (cycle/moment), de composite-secties en het
 * Just Now-paneel.
 */
@Component({
  selector: 'hd-cycle-cards',
  templateUrl: './hd-cycle-cards.component.html',
  styleUrl: './hd-cycle-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateLineComponent],
})
export class HdCycleCardsComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly slots = input.required<readonly HdCycleCardSlot[]>();
  /** `false` = dichtgeklapt: niets renderen én niets bouwen. */
  readonly visible = input(true);

  /** Klik op een verwijsregel: de aanroeper scrolt de kaart met dit anker in beeld. */
  readonly referenceClicked = output<string>();

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly lineVmFactory = computed(() =>
    createHdTransitGateLineVmFactory({
      lang: this.lang(),
      gateInfo: this.gateInfo,
      planetInfo: this.planetInfo,
      transloco: this.transloco,
    }),
  );

  protected readonly cards = computed<readonly CardVm[]>(() => {
    if (!this.visible()) {
      return [];
    }
    const lang = this.lang();
    const lineVmFor = this.lineVmFactory();
    return this.slots().map(slot => {
      const source = slot.source;
      const gateLine = `${source.gate}.${source.line}`;
      const planetName = this.planetName(source, lang);
      return {
        slot,
        lineVm: slot.first ? lineVmFor(source.gate, source.line, source.planetId, source.side, source.origin) : null,
        planetName,
        seeAboveLabel: this.transloco.translate(`${T}.seeAbove`, { gateLine }, lang),
        seeAboveTooltip: this.transloco.translate(`${T}.seeAboveTooltip`, { planet: planetName, gateLine }, lang),
      };
    });
  });

  /** Verwijsregel: de kaart staat hoger in dezelfde scrollcontainer; de aanroeper scrolt. */
  protected onReferenceClick(anchorId: string): void {
    this.referenceClicked.emit(anchorId);
  }

  /** De kaart met dit anker ín dit component, voor de aanroeper die scrolt. */
  static findCard(root: HTMLElement, anchorId: string): HTMLElement | null {
    return root.querySelector<HTMLElement>(`[data-card-id="${anchorId}"]`);
  }

  /** Eigen kaarten (voor hosts die binnen dit component willen zoeken). */
  findCard(anchorId: string): HTMLElement | null {
    return HdCycleCardsComponent.findCard(this.host.nativeElement, anchorId);
  }

  private planetName(source: HdCycleCardSource, lang: string): string {
    return this.planetInfo.planetInfoIn(source.planetKey, lang)?.name ?? source.planetKey;
  }
}
